import type { ReceiptUpload } from 'API';
import { reimbursementsService } from 'app/services/reimbursements.service';
import { useEffect, useState } from 'react';

interface UseTransactionReceiptsResult {
  receipts: ReceiptUpload[];
  isLoading: boolean;
  /** SEE-1601: the first fetch for this transaction has settled (true when there is nothing to fetch). */
  hasLoaded: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useTransactionReceipts = (
  highnoteTransactionEventId?: string | null,
  reimbursementTransactionId?: string | null
): UseTransactionReceiptsResult => {
  const [receipts, setReceipts] = useState<ReceiptUpload[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchReceipts = async () => {
    if (!highnoteTransactionEventId && !reimbursementTransactionId) {
      setReceipts([]);
      setHasLoaded(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await reimbursementsService.getReceiptsByTransactionId({
        highnoteTransactionEventId: highnoteTransactionEventId || undefined,
        reimbursementTransactionId: reimbursementTransactionId || undefined,
      });

      if (result?.items) {
        setReceipts(result.items as unknown as ReceiptUpload[]);
      } else {
        setReceipts([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch receipts'));
      setReceipts([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    setHasLoaded(false);
    fetchReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highnoteTransactionEventId, reimbursementTransactionId]);

  return {
    receipts,
    isLoading,
    hasLoaded,
    error,
    refetch: fetchReceipts,
  };
};
